/**
 * Loading message text rotation for analysis modals.
 * Messages are read from a data-messages JSON attribute on the target element
 * (sourced from Go's utils.LoadingMessages — no hardcoded strings here).
 */

// How long each message is displayed before cycling
const ROTATION_INTERVAL_MS = 2500;
// Duration of the fade-out transition (matches .loading-text-fade CSS)
const FADE_DURATION_MS = 400;

let rotationInterval: ReturnType<typeof setInterval> | null = null;

/** Fisher-Yates shuffle (in-place) */
function shuffle(arr: string[]): string[] {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

/**
 * Start rotating loading text on the element with the given ID.
 * Reads messages from the element's `data-messages` JSON attribute.
 * Cycles every ~2.5s with an opacity fade.
 */
export function startLoadingTextRotation(elementId: string): void {
    stopLoadingTextRotation();

    const el = document.getElementById(elementId);
    if (!el) return;

    const raw = el.getAttribute('data-messages');
    if (!raw) return;

    let messages: string[];
    try {
        messages = JSON.parse(raw);
    } catch {
        return;
    }
    if (!messages.length) return;

    messages = shuffle([...messages]);
    let index = 0;

    // Set initial message immediately
    el.textContent = messages[0];
    el.style.opacity = '1';
    index = 1;

    rotationInterval = setInterval(() => {
        // Fade out
        el.style.opacity = '0';

        setTimeout(() => {
            el.textContent = messages[index % messages.length];
            el.style.opacity = '1';
            index++;
        }, FADE_DURATION_MS);
    }, ROTATION_INTERVAL_MS);
}

/** Stop the current loading text rotation. */
export function stopLoadingTextRotation(): void {
    if (rotationInterval !== null) {
        clearInterval(rotationInterval);
        rotationInterval = null;
    }
}
